import { FC, MouseEventHandler, ReactElement, ReactNode } from 'react';
import { BadgeProps, SvgIcon } from '@mui/material';

import CrossSvg from '../../icons/Cross.svg';

import {
	MplChipBadgeStyled,
	MplChipStyled,
	TMplChipVariant,
} from './MplChip.styled';

export interface IMplChipProps {
	label?: ReactNode;
	icon?: ReactElement;
	variant?: TMplChipVariant;
	bgColor?: string;
	textColor?: string;
	/** Render as a circle — handy for icon-only chips. */
	circle?: boolean;
	/** Add a subtle box shadow. */
	elevated?: boolean;
	onClick?: MouseEventHandler<HTMLDivElement>;
	/** Renders a trailing cross-icon delete affordance; called when it's clicked. */
	onDelete?: () => void;
	/** data-testid for the delete affordance, when `onDelete` is set. */
	deleteTestId?: string;
	/** Content of the corner badge (e.g. an active-options count). */
	badgeContent?: ReactNode;
	/** Color of the corner badge. */
	badgeColor?: BadgeProps['color'];
	className?: string;
	dataTestId?: string;
}

export const MplChip: FC<IMplChipProps> = ({
	label,
	icon,
	variant = 'default',
	bgColor,
	textColor,
	circle = false,
	elevated = false,
	onClick,
	onDelete,
	deleteTestId,
	badgeContent,
	badgeColor = 'primary',
	className,
	dataTestId,
}) => {
	const chip = (
		<MplChipStyled
			className={className}
			ownerState={{
				chipVariant: variant,
				bgColor,
				textColor,
				circle,
				elevated,
				hasLabel: label != null,
			}}
			label={label ?? null}
			icon={icon}
			onClick={onClick}
			onDelete={onDelete}
			deleteIcon={
				onDelete ? (
					<SvgIcon component={CrossSvg} data-testid={deleteTestId} />
				) : undefined
			}
			clickable={Boolean(onClick)}
			data-testid={dataTestId}
		/>
	);

	if (badgeContent != null) {
		return (
			<MplChipBadgeStyled badgeContent={badgeContent} color={badgeColor}>
				{chip}
			</MplChipBadgeStyled>
		);
	}

	return chip;
};
